/**
 * Shared TipTap helpers for persisted clinical note content.
 *
 * These nodes are editor-only UI affordances. Some legacy notes were saved with
 * them, so runtimes must tolerate them on read, but they should be stripped
 * before note content is persisted again.
 */

export interface TiptapMarkLike {
  type?: string;
  attrs?: Record<string, unknown>;
}

export interface TiptapNodeLike {
  type?: string;
  attrs?: Record<string, unknown>;
  text?: string;
  marks?: TiptapMarkLike[];
  content?: TiptapNodeLike[];
}

export interface TiptapDocumentLike extends TiptapNodeLike {
  type: "doc";
  content?: TiptapNodeLike[];
}

export interface ClinicalNoteTextEntryLike {
  text?: string | null;
}

export const ClinicalNoteTransientUiNodeAttributes = {
  hashtag: {
    query: "",
    creatorSessionId: null,
  },
  "slash-suggestion": {
    query: "",
    creatorSessionId: null,
  },
  "ai-scribe": {},
  "letter-suggestion": {
    query: "",
  },
  imagingSuggestion: {
    query: "",
  },
} as const;

/**
 * Legacy editor nodes that were persisted before the binary schema fully mirrored
 * the editor runtime. Keep them out of the transient-stripping set so migrations
 * can backfill missing binaries without dropping historical note content.
 */
export const ClinicalNoteLegacyPersistedNodeAttributes = {
  "at-mention": {
    query: "",
    creatorSessionId: null,
  },
  "rx-suggestion": {
    query: null,
  },
  "lab-suggestion": {
    query: null,
  },
  "supplements-suggestion": {},
} as const;

export const ClinicalNoteTransientUiNodeTypes = Object.keys(
  ClinicalNoteTransientUiNodeAttributes,
) as Array<keyof typeof ClinicalNoteTransientUiNodeAttributes>;

export const ClinicalNoteTransientUiNodeAllowedAttrs = Object.fromEntries(
  Object.entries(ClinicalNoteTransientUiNodeAttributes).map(([nodeType, attrs]) => [
    nodeType,
    Object.keys(attrs),
  ]),
) as Record<keyof typeof ClinicalNoteTransientUiNodeAttributes, string[]>;

export const ClinicalNoteLegacyPersistedNodeAllowedAttrs = Object.fromEntries(
  Object.entries(ClinicalNoteLegacyPersistedNodeAttributes).map(([nodeType, attrs]) => [
    nodeType,
    Object.keys(attrs),
  ]),
) as Record<keyof typeof ClinicalNoteLegacyPersistedNodeAttributes, string[]>;

export const ClinicalNoteMeaningfulNonTextNodeTypes = [
  "vitals",
  "date",
  "icd10-tag",
  "file",
  "attachment",
  "tag",
  "labs-tag",
  "imaging-tag",
  "rx-tag",
  "supplements-tag",
  "referral-tag",
  "letter-tag",
  "resolve",
  "fullscript-labs",
] as const;

const CLINICAL_NOTE_TRANSIENT_UI_NODE_TYPE_SET = new Set<string>(ClinicalNoteTransientUiNodeTypes);
const CLINICAL_NOTE_MEANINGFUL_NON_TEXT_NODE_TYPE_SET = new Set<string>(
  ClinicalNoteMeaningfulNonTextNodeTypes,
);

export const EMPTY_CLINICAL_NOTE_DOCUMENT: TiptapDocumentLike = { type: "doc", content: [] };

export function doesClinicalNoteNodeHaveMeaningfulContent(node: TiptapNodeLike): boolean {
  if (
    typeof node.type === "string" &&
    CLINICAL_NOTE_MEANINGFUL_NON_TEXT_NODE_TYPE_SET.has(node.type)
  ) {
    return true;
  }

  if (node.type === "text" && typeof node.text === "string" && node.text.trim().length > 0) {
    return true;
  }

  return (node.content ?? []).some((child) => doesClinicalNoteNodeHaveMeaningfulContent(child));
}

export function doesClinicalNoteDocumentHaveMeaningfulContent(
  doc: TiptapDocumentLike | null | undefined,
): boolean {
  if (!doc || doc.type !== "doc" || !Array.isArray(doc.content)) {
    return false;
  }

  return doc.content.some((node) => doesClinicalNoteNodeHaveMeaningfulContent(node));
}

export function getLatestClinicalNoteText(
  note: Array<ClinicalNoteTextEntryLike | null | undefined> | undefined,
): string | undefined {
  if (!note || note.length === 0) {
    return undefined;
  }

  const candidate = [...note].reverse().find((entry) => {
    if (!entry || typeof entry.text !== "string") {
      return false;
    }

    return entry.text.trim().length > 0;
  });

  return candidate?.text ?? undefined;
}

function hasSameTextFormatting(a: TiptapNodeLike, b: TiptapNodeLike): boolean {
  return (
    JSON.stringify(a.marks ?? []) === JSON.stringify(b.marks ?? []) &&
    JSON.stringify(a.attrs ?? {}) === JSON.stringify(b.attrs ?? {})
  );
}

function coalesceAdjacentTextNodes(content: TiptapNodeLike[]): TiptapNodeLike[] {
  return content.reduce<TiptapNodeLike[]>((acc, node) => {
    const previous = acc.at(-1);
    if (
      previous?.type === "text" &&
      node.type === "text" &&
      typeof previous.text === "string" &&
      typeof node.text === "string" &&
      hasSameTextFormatting(previous, node)
    ) {
      previous.text += node.text;
      return acc;
    }

    acc.push(node);
    return acc;
  }, []);
}

function stripTransientUiNodesFromContent(content: TiptapNodeLike[]): TiptapNodeLike[] {
  const stripped = content.flatMap((node) => {
    if (!node || typeof node.type !== "string") {
      return [];
    }

    if (CLINICAL_NOTE_TRANSIENT_UI_NODE_TYPE_SET.has(node.type)) {
      return [];
    }

    if (!Array.isArray(node.content)) {
      return [node];
    }

    return [{ ...node, content: stripTransientUiNodesFromContent(node.content) }];
  });

  return coalesceAdjacentTextNodes(stripped);
}

function hasTransientUiNodeInContent(content: TiptapNodeLike[]): boolean {
  return content.some((node) => {
    if (!node || typeof node.type !== "string") {
      return false;
    }

    if (CLINICAL_NOTE_TRANSIENT_UI_NODE_TYPE_SET.has(node.type)) {
      return true;
    }

    return Array.isArray(node.content) ? hasTransientUiNodeInContent(node.content) : false;
  });
}

export function hasClinicalNoteTransientUiNodes(
  doc: TiptapDocumentLike | null | undefined,
): boolean {
  if (!doc || doc.type !== "doc" || !Array.isArray(doc.content)) {
    return false;
  }

  return hasTransientUiNodeInContent(doc.content);
}

export function stripClinicalNoteTransientUiNodesFromTiptapDocument<T extends TiptapDocumentLike>(
  doc: T | null | undefined,
): T | null {
  if (!doc || doc.type !== "doc" || !Array.isArray(doc.content)) {
    return null;
  }

  return {
    ...doc,
    content: stripTransientUiNodesFromContent(doc.content),
  } as T;
}

/**
 * Applies the shared persistence rules for clinical notes:
 * strip editor-only UI nodes, then collapse documents with no meaningful content.
 */
export function normalizeClinicalNoteTiptapDocumentForPersistence<T extends TiptapDocumentLike>(
  doc: T | null | undefined,
): T {
  const strippedDocument =
    stripClinicalNoteTransientUiNodesFromTiptapDocument(
      doc ?? (EMPTY_CLINICAL_NOTE_DOCUMENT as T),
    ) ?? (EMPTY_CLINICAL_NOTE_DOCUMENT as T);

  if (!doesClinicalNoteDocumentHaveMeaningfulContent(strippedDocument)) {
    return EMPTY_CLINICAL_NOTE_DOCUMENT as T;
  }

  return strippedDocument;
}

type ClinicalNoteMeaningfulNonTextNodeType =
  (typeof ClinicalNoteMeaningfulNonTextNodeTypes)[number];

/** Tag nodes show their label. The code tells two tags with one label apart. */
const TAG_KEY_ATTRS = ["code", "label"] as const;

/**
 * A vitals block keeps its measurements as attrs. One `nodeId` with newer values is different
 * content, and publish charts these values.
 *
 * Only attrs that the binary schema persists with a null default go in. Other attrs differ between
 * the editor JSON in `note[0].text` and the binary snapshot of one healthy note:
 * - `vo2Max`: the binary schema does not have it, so the binary drops the value.
 * - `weightUnit`/`heightUnit`/`temperatureUnit`: the binary encode fills the schema default
 *   ("lb", "in", "F") when the JSON has no unit. A unit change also converts the value, so the
 *   value still shows it.
 * - `heightFeet`/`heightInches`: input fields for `height`.
 */
const VITALS_KEY_ATTRS = [
  "nodeId",
  "measuredAt",
  "weight",
  "height",
  "systolic",
  "diastolic",
  "heartRate",
  "respiratoryRate",
  "spo2",
  "temperature",
] as const;

/**
 * The attrs that identify each meaningful atom node in the text comparison. Status attrs such as
 * `saved`, `isEditing`, `tempId`, or `status` change while the provider works, so they stay out.
 * Every attr here must be in `ClinicalNoteSchemaContract` with a null default. Otherwise the binary
 * snapshot drops or fills it, and a healthy note reads as stale.
 */
export const ClinicalNoteMeaningfulNodeKeyAttrs: Record<
  ClinicalNoteMeaningfulNonTextNodeType,
  readonly string[]
> = {
  vitals: VITALS_KEY_ATTRS,
  date: ["value"],
  "icd10-tag": TAG_KEY_ATTRS,
  file: ["id"],
  attachment: ["url"],
  tag: TAG_KEY_ATTRS,
  "labs-tag": TAG_KEY_ATTRS,
  "imaging-tag": TAG_KEY_ATTRS,
  "rx-tag": TAG_KEY_ATTRS,
  "supplements-tag": TAG_KEY_ATTRS,
  "referral-tag": TAG_KEY_ATTRS,
  "letter-tag": TAG_KEY_ATTRS,
  resolve: TAG_KEY_ATTRS,
  // key attrs are stringified - the `tests` array gives "[object Object]", so only nodeId can key
  "fullscript-labs": ["nodeId"],
};

function isMeaningfulNonTextNodeType(type: unknown): type is ClinicalNoteMeaningfulNonTextNodeType {
  return typeof type === "string" && CLINICAL_NOTE_MEANINGFUL_NON_TEXT_NODE_TYPE_SET.has(type);
}

/**
 * A stable key for a meaningful atom node. A Y.Doc drops null attrs and the editor JSON keeps
 * them, so a null, a missing, and an empty attr give the same key.
 */
function getMeaningfulNodeKey(type: ClinicalNoteMeaningfulNonTextNodeType, node: TiptapNodeLike) {
  const values = ClinicalNoteMeaningfulNodeKeyAttrs[type].map((attr) => {
    const value = node.attrs?.[attr];
    return value == null ? "" : String(value);
  });
  return `\u0001${type}(${values.join("\u0002")})`;
}

function collectNodeText(node: TiptapNodeLike): string {
  if (typeof node.text === "string") {
    return node.text;
  }
  // atoms carry no text - without a key, a lost vitals block or rx tag compares equal
  if (isMeaningfulNonTextNodeType(node.type)) {
    return getMeaningfulNodeKey(node.type, node);
  }
  return (node.content ?? []).map(collectNodeText).join("");
}

/** The persisted text of a note, plus a key per meaningful atom node, without whitespace. */
function getClinicalNoteTextKey(doc: TiptapDocumentLike | null | undefined): string {
  const text = collectNodeText(normalizeClinicalNoteTiptapDocumentForPersistence(doc));
  return text.replace(/\s+/g, "");
}

/**
 * True when two note documents hold the same text.
 *
 * Compares the text of the persisted form and the key attrs of meaningful atom nodes
 * (vitals measurements, dates, tags, files). Whitespace, block structure, marks, and other attrs do not count, so the editor JSON, a decoded Y.Doc snapshot, and the stored `note[0].text` of one
 * note compare equal. A snapshot that stopped updating holds different text. That is the stale
 * content a publish must refuse.
 */
export function doClinicalNoteDocumentsHaveSameText(
  first: TiptapDocumentLike | null | undefined,
  second: TiptapDocumentLike | null | undefined,
): boolean {
  return getClinicalNoteTextKey(first) === getClinicalNoteTextKey(second);
}
